// components/ui/tool-select-popover.tsx
"use client";

import * as React from "react";
import {
  Button,
  IconButton,
  Popover,
  VStack,
  Portal,
  HStack,
  Text,
  Flex,
} from "@chakra-ui/react";
import { Checkbox } from "./checkbox";
import { FiTool } from "react-icons/fi";
import * as FaIcons from "react-icons/fa";
import { useNavigate } from "@tanstack/react-router";

export interface ToolOption {
  label: string;
  value: string;
  icon: string;
}

export interface ToolSelectPopoverProps {
  tools?: ToolOption[];
  onSelect: (selectedTools: string[]) => void;
}

export const ToolSelectPopover = React.forwardRef<
  HTMLButtonElement,
  ToolSelectPopoverProps
>(({ tools = [], onSelect }, ref) => {
  const [selectedTools, setSelectedTools] = React.useState<string[]>([]);
  const initialized = React.useRef(false);

  // ✅ 처음 로드될 때 한 번만 전체 선택
  React.useEffect(() => {
    if (!initialized.current && tools.length > 0) {
      setSelectedTools(tools.map((t) => t.value));
      initialized.current = true;
    }
  }, [tools]);

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, idx) => val === b[idx]);
  
  const prevSelectedRef = React.useRef<string[]>([]);
  
  React.useEffect(() => {
    if (!arraysEqual(prevSelectedRef.current, selectedTools)) {
      onSelect(selectedTools);
      prevSelectedRef.current = selectedTools;
    }
  }, [selectedTools, onSelect]);
  

  // ✅ 전체 선택 여부 판단
  const allToolsSelected =
    tools.length > 0 && tools.every((t) => selectedTools.includes(t.value));

  const toggleItem = (value: string) => {
    setSelectedTools((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const toggleAll = () => {
    if (allToolsSelected) {
      setSelectedTools([]);
    } else {
      setSelectedTools(tools.map((t) => t.value));
    }
  };

  const navigate = useNavigate();
  const toolSetting = () => {
    navigate({ to: "/tools" }); // ✅ navigates to /tools route
  };

  return (
    <Popover.Root positioning={{ placement: "top-end" }}>
      <Popover.Trigger asChild>
        <Button
          ref={ref}
          width="80px"
          variant="ghost"
          borderRadius="2xl"
          border="1px solid #e2e8f0"
          size="sm"
          px={3}
        >
          <Flex align="center" gap={1}>
            <FiTool />
            <Text>도구</Text>
          </Flex>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content minW="300px">
            <Popover.CloseTrigger />
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <Popover.Body>
              <VStack align="stretch" gap={2}>
                <HStack justify="space-between">
                  <Text fontWeight="bold" fontSize="sm">
                    도구
                  </Text>
                  <IconButton
                    aria-label="도구"
                    variant="ghost"
                    size="sm"
                    onClick={toolSetting}
                  >
                    <FiTool />
                  </IconButton>
                </HStack>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={toggleAll}
                >
                  {allToolsSelected ? "전체 해제" : "전체 선택"}
                </Button>
                {tools.map((tool) => (
                  <HStack
                    key={tool.value}
                    justify="space-between"
                    w="100%"
                    gap={2}
                  >
                    <HStack>
                    {
                      FaIcons[tool.icon as keyof typeof FaIcons]
                        ? React.createElement(FaIcons[tool.icon as keyof typeof FaIcons])
                        : <FiTool />
                    }
                    <Text>{tool.label}</Text>
                    </HStack>
                    <Checkbox
                      checked={selectedTools.includes(tool.value)} // ✅
                      onChange={() => toggleItem(tool.value)}        // ✅
                    />
                  </HStack>
                ))}

              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
});
